import { GraduationCap, FileCheck2, Truck, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine } from "./Reveal";
import { scrollToId } from "../hooks/useLenis";

const STEPS = [
  {
    icon: GraduationCap,
    num: "01",
    title: "Du får ditt besked",
    text: "Under T4 får du besked om din utplacering (T6) via Umeå universitet – och information om vilket flyttbidrag som gäller för just din ort. Beloppet bestäms av universitetet och varierar mellan Östersund, Sundsvall och Luleå.",
  },
  {
    icon: FileCheck2,
    num: "02",
    title: "Vi skickar offert & underlag",
    text: "Fyll i vår offertförfrågan så återkommer vi med fast pris och det underlag som universitetet behöver för bidraget. Inga dolda kostnader, inga överraskningar.",
  },
  {
    icon: Truck,
    num: "03",
    title: "Vi flyttar – skolan betalar",
    text: "På flyttdagen bär, kör och ställer vi in allt i ditt nya boende. Fakturan skickas direkt till universitetet enligt bidragsbeslutet – du betalar ingenting ur egen ficka.",
  },
];

const HOSPITALS = [
  { city: "Östersund", place: "Östersunds sjukhus", amount: "15 000 kr" },
  { city: "Sundsvall", place: "Sundsvalls sjukhus", amount: "Upp till 10 000 kr" },
  { city: "Luleå", place: "Sunderby sjukhus", amount: "Upp till 15 000 kr" },
];

export const GrantSection = () => (
  <section id="flyttbidrag" data-testid="grant-section" className="relative bg-[#fafafa] dot-grid py-24 sm:py-36 overflow-hidden">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14 lg:gap-20">
        <div className="lg:sticky lg:top-28 self-start text-center lg:text-left">
          <Reveal>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#16a34a] mb-5">Flyttbidrag & utplacering</p>
          </Reveal>
          <h2 className="font-display font-extrabold tracking-tight leading-[1.05] text-[clamp(1.9rem,8vw,3.75rem)] text-[#0b0f19]">
            <MaskedLine>{"Flyttbidraget "}</MaskedLine>
            <MaskedLine delay={0.1}>täcker <span className="text-[#16a34a]">allt.</span></MaskedLine>
          </h2>
          <Reveal delay={0.35}>
            <p className="mt-6 text-base leading-relaxed text-slate-600 max-w-md mx-auto lg:mx-0">
              Utplacering via läkarprogrammet eller praktik i annan stad? Vi vet exakt vad
              skolan behöver och sköter allt – från offert och underlag till färdig faktura.
            </p>
            <button
              data-testid="grant-cta-button"
              onClick={() => scrollToId("#offert")}
              className="group mt-8 inline-flex items-center gap-2 font-semibold text-[#16a34a] hover:text-[#15803d] transition-colors"
            >
              Starta din offertförfrågan
              <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>

        <div className="flex flex-col gap-5">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.12}>
              <div
                data-testid={`grant-step-${s.num}`}
                className="group bg-white border border-slate-200 rounded-2xl p-7 sm:p-9 hover:border-[#16a34a]/50 hover:shadow-lg transition-[border-color,box-shadow] duration-300"
              >
                <div className="flex items-start justify-between gap-6">
                  <div className="flex items-start gap-5">
                    <span className="font-mono2 font-bold text-sm text-[#16a34a] mt-1">{s.num}</span>
                    <div>
                      <h3 className="font-display font-bold text-xl sm:text-2xl text-[#0b0f19]">{s.title}</h3>
                      <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-600">{s.text}</p>
                    </div>
                  </div>
                  <s.icon className="w-7 h-7 text-slate-300 group-hover:text-[#16a34a] transition-colors duration-300 shrink-0" />
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.3}>
            <div data-testid="grant-destinations" className="rounded-2xl bg-[#0b0f19] p-7 sm:p-9 grain relative">
              <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-green-400 mb-5">Våra utplaceringsorter</p>
              <div className="flex flex-col divide-y divide-white/10">
                {HOSPITALS.map((h) => (
                  <div key={h.city} className="flex items-center justify-between py-4 group cursor-default">
                    <span className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight group-hover:text-[#16a34a] transition-colors duration-300">
                      Umeå → {h.city}
                    </span>
                    <span className="text-right shrink-0">
                      <span className="block font-mono2 text-[11px] text-slate-400 tracking-wider">{h.place}</span>
                      <span className="block font-mono2 text-[11px] text-green-400 font-bold tracking-wider mt-1">{h.amount}</span>
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-white/10 flex flex-col gap-3">
                <p data-testid="grant-note-sundsvall" className="text-xs leading-relaxed text-slate-400">
                  <span className="font-semibold text-slate-200">Sundsvall – Region Västernorrland:</span> Skattefri flyttersättning upp till 10 000 kr. Tre anbud inhämtas och ersättning ges för det billigaste anbudet.
                </p>
                <p data-testid="grant-note-lulea" className="text-xs leading-relaxed text-slate-400">
                  <span className="font-semibold text-slate-200">Luleå/Sunderby – Region Norrbotten:</span> Flyttbidrag för bohagstransport upp till 15 000 kr, skattefritt.
                </p>
              </div>
            </div>
          </Reveal>

        </div>
      </div>
    </div>
  </section>
);
